import React, { useState, useEffect, useCallback } from "react";
import { auditService } from "../../Services/auditService";
import * as S from "./AuditStyle";
import { FaHistory, FaFilter, FaBroom, FaSearch } from "react-icons/fa";

const ITEMS_PER_PAGE = 10;

export default function Audit() {
  // Estado para controlar o modo de visualização (admin ou client)
  const [viewMode, setViewMode] = useState("admin"); // 'admin' | 'client'

  const [audits, setAudits] = useState([]);
  const [totalAudits, setTotalAudits] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchTermInput, setSearchTermInput] = useState("");
  const [startDateInput, setStartDateInput] = useState("");
  const [endDateInput, setEndDateInput] = useState("");

  const [appliedFilters, setAppliedFilters] = useState({
    searchTerm: "",
    startDate: "",
    endDate: "",
  });

  const totalPages = Math.ceil(totalAudits / ITEMS_PER_PAGE);

  const fetchAudits = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        limit: ITEMS_PER_PAGE,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
        ...appliedFilters,
      };

      let data;
      // Decide qual serviço chamar com base no viewMode
      if (viewMode === "admin") {
        data = await auditService.searchAudits(params);
      } else {
        data = await auditService.searchClientAudits(params);
      }

      setAudits(data.audits || []);
      setTotalAudits(data.totalCount || 0);
    } catch (error) {
      console.error("Erro ao buscar auditorias:", error);
      setAudits([]);
      setTotalAudits(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, appliedFilters, viewMode]); // Adicionado viewMode nas dependências

  useEffect(() => {
    fetchAudits();
  }, [fetchAudits]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setAppliedFilters({
      searchTerm: searchTermInput,
      startDate: startDateInput,
      endDate: endDateInput,
    });
  };

  const handleResetFilters = () => {
    setCurrentPage(1);
    setSearchTermInput("");
    setStartDateInput("");
    setEndDateInput("");
    setAppliedFilters({ searchTerm: "", startDate: "", endDate: "" });
  };

  const handleModeChange = (mode) => {
    if (mode !== viewMode) {
      setViewMode(mode);
      setCurrentPage(1); // Reseta a página ao trocar de aba
      // Opcional: Resetar filtros também se desejar
      // handleResetFilters();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString("pt-BR");
  };

  return (
    <S.AuditContainer>
      <S.Header>Painel de Auditoria</S.Header>

      {/* Seletor de Modos */}
      <S.ToggleContainer>
        <S.ToggleButton
          active={viewMode === "admin"}
          onClick={() => handleModeChange("admin")}
        >
          Administrativo
        </S.ToggleButton>
        <S.ToggleButton
          active={viewMode === "client"}
          onClick={() => handleModeChange("client")}
        >
          Clientes
        </S.ToggleButton>
      </S.ToggleContainer>

      <S.Card>
        <S.CardHeader>
          <FaHistory color="#B17D2A" />
          {viewMode === "admin"
            ? "Histórico Administrativo"
            : "Histórico de Clientes"}
        </S.CardHeader>

        <S.FilterContainer onSubmit={handleFilterSubmit}>
          <S.FormGroup>
            <label>Buscar</label>
            <input
              type="text"
              value={searchTermInput}
              onChange={(e) => setSearchTermInput(e.target.value)}
              placeholder={
                viewMode === "admin"
                  ? "Nome do admin ou ação..."
                  : "Nome do cliente ou ação..."
              }
            />
          </S.FormGroup>
          <S.FormGroup>
            <label>Data de Início</label>
            <input
              type="date"
              value={startDateInput}
              onChange={(e) => setStartDateInput(e.target.value)}
            />
          </S.FormGroup>
          <S.FormGroup>
            <label>Data Final</label>
            <input
              type="date"
              value={endDateInput}
              onChange={(e) => setEndDateInput(e.target.value)}
            />
          </S.FormGroup>
          <S.ButtonContainer>
            <S.ActionButton type="submit">
              <FaSearch />
              Filtrar
            </S.ActionButton>
            <S.ActionButton
              type="button"
              secondary
              onClick={handleResetFilters}
            >
              <FaBroom />
              Limpar
            </S.ActionButton>
          </S.ButtonContainer>
        </S.FilterContainer>

        <S.TableContainer>
          <S.AuditTable>
            <thead>
              <tr>
                {/* Coluna dinâmica dependendo do modo */}
                <th>{viewMode === "admin" ? "Admin" : "Cliente"}</th>
                <th>Ação Realizada</th>
                <th>Data</th>
                <th>Endereço IP</th>
                <th>Localização</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan="5"
                    style={{
                      textAlign: "center",
                      padding: "50px",
                      color: "#6c757d",
                    }}
                  >
                    Carregando dados...
                  </td>
                </tr>
              ) : audits.length > 0 ? (
                audits.map((audit) => (
                  <tr key={audit.id}>
                    {/* Exibe AdminName se for modo admin, ClientName se for modo cliente */}
                    <td>
                      {viewMode === "admin"
                        ? audit.adminName || "N/A"
                        : audit.clientName || "N/A"}
                    </td>
                    <td>{audit.actionDone}</td>
                    <td>{formatDate(audit.dateCreated)}</td>
                    <td>{audit.ipAddress}</td>
                    <td>
                      {`${audit.cityName || ""}, ${audit.stateName || ""} - ${
                        audit.countryCode || ""
                      }`
                        .replace(/^, /, "")
                        .replace(/ - $/, "")}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    style={{
                      textAlign: "center",
                      padding: "50px",
                      color: "#6c757d",
                    }}
                  >
                    Nenhum registro encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </S.AuditTable>
        </S.TableContainer>

        {!isLoading && totalAudits > ITEMS_PER_PAGE && (
          <S.PaginationContainer>
            <S.PageButton
              onClick={() => setCurrentPage((p) => p - 1)}
              disabled={currentPage === 1}
            >
              Anterior
            </S.PageButton>
            <span>
              Página {currentPage} de {totalPages}
            </span>
            <S.PageButton
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={currentPage >= totalPages}
            >
              Próxima
            </S.PageButton>
          </S.PaginationContainer>
        )}
      </S.Card>
    </S.AuditContainer>
  );
}
